<template>
    <div class="table-answer-view">
        <div class="tav-scroll">
            <table class="tav-table">
                <thead>
                    <tr>
                        <th v-if="withLabels" class="tav-label"></th>
                        <!-- i18n-validator-ignore-next-line: runtime column labels -->
                        <th v-for="(col, j) in data.columns" :key="`h-${j}`">{{ col.label }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(row, i) in shownRows"
                        :key="`r-${i}`"
                        :class="{ 'tav-summary': row.kind === 'summary' }"
                    >
                        <!-- i18n-validator-ignore-next-line: runtime row label -->
                        <th v-if="withLabels" class="tav-label" scope="row">{{ row.label }}</th>
                        <td
                            v-for="(cell, j) in row.cells"
                            :key="`c-${i}-${j}`"
                            :class="{
                                'is-formula': cell.formula,
                                'is-locked': cell.locked,
                                'is-number': data.columns[j].type === 'number',
                            }"
                        >
                            {{ cell.text }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div class="tav-footer">
            <button
                v-if="hiddenCount > 0 || expanded"
                type="button"
                class="tav-link"
                @click="expanded = !expanded"
            >
                {{
                    expanded
                        ? t("inbox-message-table_show_less")
                        : t("inbox-message-table_show_all", [dataRowCount])
                }}
            </button>
            <button type="button" class="tav-link" @click="copy">
                <span class="mdi" :class="copied ? 'mdi-check' : 'mdi-content-copy'"></span>
                {{ copied ? t("inbox-message-table_copied") : t("inbox-message-table_copy") }}
            </button>
        </div>
    </div>
</template>

<script>
import { tableDisplayRows, tableHasRowLabels, tableToTsv } from "../../helpers/tableQuestion";

/* Compact read-only view of a stored table answer {columns, rows, values, summary}. */
export default {
    name: "TableAnswerView",
    props: {
        data: { type: Object, required: true },
        limit: { type: Number, default: 6 },
    },
    data() {
        return { expanded: false, copied: false };
    },
    computed: {
        rows() {
            return tableDisplayRows(this.data);
        },
        dataRows() {
            return this.rows.filter((r) => r.kind === "data");
        },
        dataRowCount() {
            return this.dataRows.length;
        },
        hiddenCount() {
            return Math.max(0, this.dataRowCount - this.limit);
        },
        // Summary rows always stay visible under the shown data rows.
        shownRows() {
            const summary = this.rows.filter((r) => r.kind === "summary");
            const data = this.expanded ? this.dataRows : this.dataRows.slice(0, this.limit);
            return data.concat(summary);
        },
        withLabels() {
            return tableHasRowLabels(this.data);
        },
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        copy() {
            const text = tableToTsv(this.data);
            const done = () => {
                this.copied = true;
                clearTimeout(this.copiedTimeout);
                this.copiedTimeout = setTimeout(() => {
                    this.copied = false;
                }, 2000);
            };
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(done).catch(() => this.legacyCopy(text, done));
            } else {
                this.legacyCopy(text, done);
            }
        },
        legacyCopy(text, done) {
            const area = document.createElement("textarea");
            area.value = text;
            area.setAttribute("readonly", "");
            area.style.position = "fixed";
            area.style.opacity = "0";
            document.body.appendChild(area);
            area.select();
            try {
                document.execCommand("copy");
                done();
            } finally {
                document.body.removeChild(area);
            }
        },
    },
    beforeDestroy() {
        clearTimeout(this.copiedTimeout);
    },
};
</script>

<style scoped lang="less">
.table-answer-view {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
}

.tav-scroll {
    overflow-x: auto;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    max-width: 100%;
}

.tav-table {
    border-collapse: collapse;
    font-size: 12px;
    width: 100%;

    th,
    td {
        padding: 4px 8px;
        border-bottom: 1px solid #f3f4f6;
        border-right: 1px solid #f3f4f6;
        white-space: nowrap;
        text-align: left;
    }

    thead th {
        background: #f9fafb;
        color: #4d5761;
        font-weight: 500;
    }

    .tav-label {
        background: #f9fafb;
        color: #384250;
        font-weight: 500;
    }

    td.is-number {
        text-align: right;
        font-variant-numeric: tabular-nums;
    }

    td.is-formula,
    td.is-locked {
        background: #f6f8fa;
    }

    td.is-formula {
        color: #0b6b3a;
    }

    .tav-summary th,
    .tav-summary td {
        border-top: 1px solid #e5e7eb;
        font-weight: 500;
    }

    tr:last-child th,
    tr:last-child td {
        border-bottom: 0;
    }
}

.tav-footer {
    display: flex;
    gap: 12px;
}

.tav-link {
    border: 0;
    background: transparent;
    padding: 0;
    font-size: 12px;
    color: var(--primary, #2aaa4c);
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
</style>
